import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { API } from '../utils/api';
import {
  Shield, Clock, Heart, CheckCircle, Star, ChevronDown, ChevronUp,
  Calendar, BadgeCheck, Sparkles, ArrowRight, Mail, Loader2, Lock,
  CreditCard, Video, FileText, Users, Award
} from 'lucide-react';

const ASESOR_NAME = 'Héctor Marín';
const PRECIO_CLP = 35000;
const DURATION_MIN = 30;

// ---------- Helpers ----------
const fmtPrice = (n) => `$${n.toLocaleString('es-CL')}`;
const TZ_LABEL = 'hora Chile';

function groupSlotsByDay(slots) {
  const groups = {};
  slots.forEach(iso => {
    const d = new Date(iso);
    const key = d.toLocaleDateString('es-CL', { weekday: 'long', day: '2-digit', month: 'long' });
    if (!groups[key]) groups[key] = [];
    groups[key].push(iso);
  });
  return groups;
}
const fmtTime = (iso) => new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });

// ============================================================================
// LANDING (paso 0)
// ============================================================================
function HeroLanding({ onStart }) {
  return (
    <section className="relative bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 pt-20 pb-16">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.08),transparent_50%)] pointer-events-none" />
      <div className="relative container mx-auto px-4 max-w-5xl">
        <div className="flex items-center gap-2 mb-5">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-300 text-xs font-semibold tracking-wide">
            <Sparkles className="w-3.5 h-3.5" />
            ASESORÍA EXPERTA · 30 MIN · {fmtPrice(PRECIO_CLP)} CLP
          </span>
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-5">
          Antes de elegir un tratamiento,<br />
          <span className="text-emerald-400">conversa con un experto.</span>
        </h1>
        <p className="text-lg text-slate-300 max-w-2xl mb-3 leading-relaxed">
          30 minutos con <strong className="text-white">{ASESOR_NAME}</strong>, terapeuta con 15 años de experiencia en Chile y Latinoamérica. Analizamos tu caso real (sustancia, historia, intentos previos, contexto) y te entregamos una <strong className="text-white">recomendación personalizada</strong> del camino terapéutico más probable de funcionar.
        </p>
        <p className="text-sm text-slate-400 max-w-2xl mb-8">
          No vendemos tratamientos. Te orientamos para que elijas bien. Sin perder tiempo en centros que no son para ti.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center mb-10">
          <Button
            onClick={onStart}
            data-testid="hero-cta-reservar"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-7 py-6 text-base rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Reservar mi asesoría · {fmtPrice(PRECIO_CLP)}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <span className="text-xs text-slate-500 flex items-center gap-2">
            <Lock className="w-3.5 h-3.5" /> Pago seguro con Klap · Videollamada Google Meet
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { icon: Award, txt: '15 años de experiencia' },
            { icon: Video, txt: 'Videollamada privada' },
            { icon: FileText, txt: 'Recomendación escrita' },
            { icon: Shield, txt: '100% confidencial' },
          ].map((b, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
              <b.icon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span>{b.txt}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function QueObtienes() {
  const items = [
    { t: 'Análisis clínico de tu caso', d: 'Sustancia, años de consumo, intentos previos, dependencia, patología dual, contexto familiar y económico.' },
    { t: 'Recomendación personalizada', d: 'Residencial vs ambulatorio vs online. Enfoque terapéutico (CBT, holístico, integrativo). Duración realista del proceso.' },
    { t: 'Centros que matchean contigo', d: '2-3 opciones reales del directorio según tu presupuesto, región y necesidad clínica. Te ahorramos el filtrar.' },
    { t: 'Plan de acción por email', d: 'Tras la sesión recibes un PDF con el resumen del análisis y los próximos pasos concretos para ti.' },
  ];
  return (
    <section className="bg-slate-900 py-16">
      <div className="container mx-auto px-4 max-w-5xl">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">¿Qué obtienes en 30 minutos?</h2>
        <p className="text-slate-400 mb-10 max-w-2xl">No es una llamada de venta ni un cuestionario genérico. Es una sesión clínica corta y profunda diseñada para que salgas con claridad.</p>
        <div className="grid sm:grid-cols-2 gap-5">
          {items.map((it, i) => (
            <div key={i} className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 hover:border-emerald-500/40 transition">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <h3 className="font-bold text-white text-lg">{it.t}</h3>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">{it.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ParaQuien() {
  const audiencias = [
    { t: 'Familiar preocupado/a', d: 'No sabes si llevarlo a un residencial, esperar o probar ambulatorio. Necesitas una segunda opinión profesional antes de decidir.' },
    { t: 'Persona considerando tratamiento', d: 'Reconoces que necesitas ayuda pero no sabes qué tipo, cuánto cuesta realmente, ni dónde empezar.' },
    { t: 'Profesional derivando', d: 'Trabajas en salud o RRHH y quieres orientación clínica externa para un caso particular.' },
  ];
  return (
    <section className="bg-slate-950 py-16">
      <div className="container mx-auto px-4 max-w-5xl">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-10">¿Para quién es esto?</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {audiencias.map((a, i) => (
            <div key={i} className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-xl p-6">
              <Users className="w-7 h-7 text-emerald-400 mb-3" />
              <h3 className="font-bold text-white text-lg mb-2">{a.t}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{a.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TuTerapeuta() {
  return (
    <section className="bg-slate-900 py-16">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="bg-gradient-to-br from-emerald-500/10 to-slate-900 border border-emerald-500/20 rounded-2xl p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500/40 flex items-center justify-center text-emerald-300 font-bold text-xl">
              HM
            </div>
            <div>
              <p className="text-xs text-emerald-400 font-semibold tracking-wide mb-0.5">TU ASESOR</p>
              <h3 className="text-2xl font-bold text-white">{ASESOR_NAME}</h3>
            </div>
          </div>
          <p className="text-slate-300 leading-relaxed mb-3">
            15 años de experiencia clínica en evaluación de adicciones en Chile y Latinoamérica. Ha guiado a cientos de familias y personas a elegir el camino terapéutico correcto, evitando recaídas costosas por tratamientos mal indicados.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            {['Adicciones', 'Patología dual', 'Familias', 'Pasta base', 'Alcohol', 'Cocaína'].map(tag => (
              <span key={tag} className="px-3 py-1 bg-slate-800/60 border border-slate-700 rounded-full text-xs text-slate-300">{tag}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ComoFunciona({ onStart }) {
  const pasos = [
    { n: 1, t: 'Reserva y paga', d: 'Eliges un horario disponible y pagas con Klap. Recibes confirmación inmediata por email con el link de Google Meet.' },
    { n: 2, t: 'Llenas tu caso', d: 'Antes de la sesión nos cuentas brevemente tu situación: sustancia, años, contexto. Llega Héctor preparado para tu caso específico.' },
    { n: 3, t: 'Sesión de 30 min', d: 'Videollamada privada. Te escuchamos, evaluamos y te damos recomendación clara. Sin presión, sin venta.' },
    { n: 4, t: 'Plan por email', d: 'En las 24h siguientes recibes el resumen con tu plan de acción y los centros recomendados.' },
  ];
  return (
    <section className="bg-slate-950 py-16">
      <div className="container mx-auto px-4 max-w-5xl">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-10">Cómo funciona</h2>
        <div className="grid md:grid-cols-4 gap-5 mb-10">
          {pasos.map(p => (
            <div key={p.n} className="bg-slate-900/60 rounded-xl p-5 border border-slate-800">
              <div className="text-3xl font-bold text-emerald-400 mb-2">0{p.n}</div>
              <h3 className="font-bold text-white mb-2">{p.t}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
        <div className="text-center">
          <Button onClick={onStart} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-7 py-5 text-base rounded-xl">
            Reservar ahora · {fmtPrice(PRECIO_CLP)}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
}

function Testimonios() {
  const t = [
    { txt: 'Estaba perdido entre tantas opciones. La asesoría me orientó a un residencial conductual con enfoque en recaídas. Hoy llevo 5 meses limpio.', autor: 'Juan P., 34 años', det: 'Cocaína' },
    { txt: 'Pensaba que un ambulatorio bastaba pero Héctor vio que mi caso requería holístico con terapia familiar. 7 meses sobria.', autor: 'María A., 28 años', det: 'Alcohol' },
    { txt: 'Mi hijo consumía pasta base. No sabíamos si gratuito o privado. Nos orientó a un residencial accesible. Cambios reales.', autor: 'Carlos R.', det: 'Familiar' },
  ];
  return (
    <section className="bg-slate-900 py-16">
      <div className="container mx-auto px-4 max-w-5xl">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-10">Lo que dicen quienes ya lo hicieron</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {t.map((it, i) => (
            <div key={i} className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
              </div>
              <p className="text-slate-300 text-sm leading-relaxed italic mb-4">"{it.txt}"</p>
              <p className="text-xs font-semibold text-white">{it.autor}</p>
              <p className="text-xs text-slate-500">{it.det}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(null);
  const faqs = [
    { q: '¿Por qué pagar por una orientación?', r: 'Porque es atención clínica real, no un chat de venta. Héctor reserva 30 min exclusivos para tu caso, prepara el análisis previo y te entrega recomendación escrita. Te ahorra meses (y mucho dinero) en tratamientos equivocados.' },
    { q: '¿Qué pasa si no puedo asistir?', r: 'Puedes reagendar sin costo escribiendo a hector@sinadicciones.org con al menos 12h de anticipación. No realizamos reembolsos, pero el horario se reagenda 1 vez.' },
    { q: '¿La sesión es por videollamada?', r: `Sí, por Google Meet. Te llega el link al confirmar el pago. Dura ${DURATION_MIN} minutos exactos.` },
    { q: '¿Me van a vender algo?', r: 'No. Héctor solo orienta. Los centros recomendados son del directorio público de SinAdicciones.org. Tú decides con quién avanzar (o si avanzar).' },
    { q: '¿Puedo agendar para un familiar?', r: 'Sí. Lo más útil es que la persona afectada también esté en la videollamada, pero si no quiere, podemos hacer la sesión solo contigo y construir un plan para acercarte a ella.' },
  ];
  return (
    <section className="bg-slate-950 py-16">
      <div className="container mx-auto px-4 max-w-3xl">
        <h2 className="text-3xl font-bold text-white mb-8 text-center">Preguntas frecuentes</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={i} className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-900 transition"
                data-testid={`faq-${i}`}
              >
                <span className="font-semibold text-white text-sm">{f.q}</span>
                {open === i ? <ChevronUp className="w-4 h-4 text-emerald-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />}
              </button>
              {open === i && (
                <div className="px-5 pb-4 text-sm text-slate-400 leading-relaxed">{f.r}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// BOOKING WIZARD
// ============================================================================
function BookingWizard({ onBack }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    nombre: '', email: '', telefono: '',
    sustancia: '', contexto: '',
  });
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch slots when reaching step 2
  useEffect(() => {
    if (step !== 2) return;
    setSlotsLoading(true);
    const today = new Date();
    const dateFrom = today.toISOString().split('T')[0];
    axios.get(`${API}/asesoria-booking/availability`, { params: { date_from: dateFrom, days: 14 } })
      .then(r => setSlots(r.data.slots || []))
      .catch(() => toast.error('No pudimos cargar los horarios disponibles. Intenta de nuevo.'))
      .finally(() => setSlotsLoading(false));
  }, [step]);

  const grouped = useMemo(() => groupSlotsByDay(slots), [slots]);

  const canStep1 = form.nombre.trim() && form.email.trim() && form.telefono.trim() && form.sustancia && form.contexto.trim().length >= 10;

  const handlePay = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);
    try {
      const startResp = await axios.post(`${API}/asesoria-booking/start`, {
        nombre: form.nombre, email: form.email, telefono: form.telefono,
        sustancia: form.sustancia, contexto: form.contexto,
        slot_iso: selectedSlot,
      });
      const { booking_id } = startResp.data;
      const payResp = await axios.post(`${API}/asesoria-booking/${booking_id}/pay`);
      const { url, token, redirect_url, checkout_url, provider } = payResp.data;

      // Klap → redirect directo
      if (provider === 'klap' && (redirect_url || checkout_url)) {
        try {
          localStorage.setItem('current_order_id', payResp.data.klap_reference_id || '');
          localStorage.setItem('current_order_provider', 'klap');
        } catch (_lsErr) { /* noop */ }
        window.location.href = redirect_url || checkout_url;
        return;
      }

      // Transbank fallback → auto-submit form
      const f = document.createElement('form');
      f.method = 'POST';
      f.action = url;
      const i = document.createElement('input');
      i.type = 'hidden'; i.name = 'token_ws'; i.value = token;
      f.appendChild(i);
      document.body.appendChild(f);
      f.submit();
    } catch (e) {
      const msg = e.response?.data?.detail || 'No pudimos procesar tu reserva. Intenta de nuevo.';
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-slate-950 min-h-screen py-12">
      <div className="container mx-auto px-4 max-w-3xl">
        <button onClick={onBack} className="text-slate-400 hover:text-white text-sm mb-6 flex items-center gap-1" data-testid="back-to-landing">
          ← Volver
        </button>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3].map(n => (
            <React.Fragment key={n}>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${step >= n ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-800 text-slate-500'}`}>
                {step > n ? <CheckCircle className="w-3.5 h-3.5" /> : <span className="w-4 h-4 rounded-full bg-current/20 flex items-center justify-center">{n}</span>}
                {n === 1 ? 'Tu caso' : n === 2 ? 'Horario' : 'Pago'}
              </div>
              {n < 3 && <div className="flex-1 h-px bg-slate-800" />}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-white mb-2">Cuéntanos tu caso</h2>
            <p className="text-slate-400 text-sm mb-6">Estos datos llegan a Héctor antes de la sesión para preparar tu análisis.</p>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Nombre completo *</Label>
                <Input data-testid="input-nombre" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Ej. María Pérez" className="bg-slate-800/60 border-slate-700 text-white" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Email *</Label>
                  <Input type="email" data-testid="input-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="tu@email.com" className="bg-slate-800/60 border-slate-700 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">Teléfono / WhatsApp *</Label>
                  <Input type="tel" data-testid="input-telefono" value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} placeholder="+56 9 ..." className="bg-slate-800/60 border-slate-700 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Sustancia o problema principal *</Label>
                <Select value={form.sustancia} onValueChange={v => setForm({ ...form, sustancia: v })}>
                  <SelectTrigger data-testid="select-sustancia" className="bg-slate-800/60 border-slate-700 text-white"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alcohol">Alcohol</SelectItem>
                    <SelectItem value="cocaina">Cocaína</SelectItem>
                    <SelectItem value="pasta_base">Pasta base</SelectItem>
                    <SelectItem value="marihuana">Marihuana</SelectItem>
                    <SelectItem value="benzo_opioides">Benzodiacepinas / Opioides</SelectItem>
                    <SelectItem value="ludopatia">Ludopatía</SelectItem>
                    <SelectItem value="patologia_dual">Patología dual</SelectItem>
                    <SelectItem value="otra">Otra / múltiples</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Contexto en 2-3 líneas * <span className="text-slate-500 text-xs ml-2">(mínimo 10 caracteres)</span></Label>
                <Textarea data-testid="input-contexto" value={form.contexto} onChange={e => setForm({ ...form, contexto: e.target.value })} placeholder="Ej. Mi hijo de 23 años consume cocaína hace 4 años, hemos intentado 2 ambulatorios sin éxito, vive en RM..." rows={4} className="bg-slate-800/60 border-slate-700 text-white resize-none" />
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!canStep1} data-testid="step1-continue" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold">
                Continuar → Elegir horario
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-white mb-2">Elige tu horario</h2>
            <p className="text-slate-400 text-sm mb-6">{DURATION_MIN} min · {TZ_LABEL} · próximos 14 días disponibles.</p>
            {slotsLoading && <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Cargando horarios...</div>}
            {!slotsLoading && slots.length === 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5 text-amber-200 text-sm">
                <p className="font-semibold mb-1">Sin horarios disponibles en este momento.</p>
                <p>Escríbenos a <a href="mailto:hector@sinadicciones.org" className="underline">hector@sinadicciones.org</a> y coordinamos un cupo manual.</p>
              </div>
            )}
            {!slotsLoading && slots.length > 0 && (
              <div className="space-y-5 max-h-[420px] overflow-y-auto pr-2">
                {Object.entries(grouped).map(([day, daySlots]) => (
                  <div key={day}>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{day}</p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {daySlots.map(iso => (
                        <button
                          key={iso}
                          onClick={() => setSelectedSlot(iso)}
                          data-testid={`slot-${iso}`}
                          className={`px-3 py-2.5 rounded-lg text-sm font-semibold transition ${
                            selectedSlot === iso
                              ? 'bg-emerald-500 text-slate-950 ring-2 ring-emerald-400'
                              : 'bg-slate-800/60 text-slate-300 hover:bg-slate-800 border border-slate-700'
                          }`}
                        >
                          {fmtTime(iso)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6 flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)} className="text-slate-300">← Atrás</Button>
              <Button onClick={() => setStep(3)} disabled={!selectedSlot} data-testid="step2-continue" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold">
                Continuar → Pago
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-white mb-2">Confirma y paga</h2>
            <p className="text-slate-400 text-sm mb-6">Te redirigimos a Klap para completar el pago. Tu sesión queda agendada al instante.</p>
            <div className="bg-slate-800/50 rounded-xl p-5 mb-5 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Cliente</span>
                <span className="text-white font-semibold text-right">{form.nombre}<br /><span className="text-slate-500 text-xs font-normal">{form.email}</span></span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Sesión con</span>
                <span className="text-white font-semibold">{ASESOR_NAME}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Fecha y hora</span>
                <span className="text-white font-semibold text-right">{new Date(selectedSlot).toLocaleString('es-CL', { weekday: 'long', day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}<br /><span className="text-slate-500 text-xs font-normal">{TZ_LABEL} · {DURATION_MIN} min</span></span>
              </div>
              <div className="flex justify-between pt-3 border-t border-slate-700">
                <span className="text-slate-300 font-semibold">Total</span>
                <span className="text-emerald-400 font-bold text-xl">{fmtPrice(PRECIO_CLP)} CLP</span>
              </div>
            </div>
            <div className="text-xs text-slate-500 mb-5 space-y-1">
              <p>• Tras el pago recibirás el link de Google Meet por email.</p>
              <p>• Puedes reagendar 1 vez con al menos 12h de anticipación. No hay reembolsos.</p>
              <p>• Pago seguro con Klap (tarjetas de crédito y débito).</p>
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(2)} className="text-slate-300" disabled={submitting}>← Atrás</Button>
              <Button onClick={handlePay} disabled={submitting} data-testid="pay-button" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 py-5">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando...</> : <><CreditCard className="w-4 h-4 mr-2" /> Pagar {fmtPrice(PRECIO_CLP)}</>}
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export function AsesoriaPage() {
  const [mode, setMode] = useState('landing'); // 'landing' | 'booking'

  return (
    <div className="min-h-screen bg-slate-950">
      <title>Asesoría Experta en Adicciones · 30 min · $35.000 | SinAdicciones.org</title>
      <meta name="description" content="30 min con Héctor Marín, terapeuta con 15 años de experiencia. Análisis de tu caso y recomendación personalizada del mejor camino terapéutico. Videollamada · Pago seguro." />
      <link rel="canonical" href="https://sinadicciones.org/asesoria" />
      <meta property="og:title" content="Asesoría Experta en Adicciones · 30 min · $35.000" />
      <meta property="og:description" content="Conversa con un experto antes de elegir un tratamiento. Análisis personalizado del caso y recomendación clara del camino terapéutico." />
      <meta property="og:url" content="https://sinadicciones.org/asesoria" />
      <meta property="og:type" content="service" />
      <meta property="og:locale" content="es_CL" />
      <meta property="og:site_name" content="SinAdicciones.org" />

      <Header />
      {mode === 'landing' ? (
        <>
          <HeroLanding onStart={() => setMode('booking')} />
          <QueObtienes />
          <ParaQuien />
          <TuTerapeuta />
          <ComoFunciona onStart={() => setMode('booking')} />
          <Testimonios />
          <FAQ />

          {/* Bottom CTA */}
          <section className="bg-gradient-to-br from-emerald-500/10 to-slate-950 py-16">
            <div className="container mx-auto px-4 max-w-3xl text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">No tomes la decisión sin tener claridad.</h2>
              <p className="text-slate-300 mb-7 leading-relaxed">{DURATION_MIN} minutos con un experto pueden ahorrarte meses de un tratamiento equivocado.</p>
              <Button
                onClick={() => setMode('booking')}
                data-testid="bottom-cta-reservar"
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-8 py-6 text-base rounded-xl"
              >
                Reservar mi asesoría · {fmtPrice(PRECIO_CLP)}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </section>
        </>
      ) : (
        <BookingWizard onBack={() => setMode('landing')} />
      )}
      <Footer />
    </div>
  );
}

export default AsesoriaPage;
